'use client'
import { clsx } from 'clsx'
import { AnimatePresence, domAnimation, LazyMotion, m as motion, useInView } from 'framer-motion'

import { FC, useRef } from 'react'

import { IChildren, IClassName } from '@/app/shared/interfaces/common.interface'
import { generateFonts } from '@/app/shared/ui/title/constants'

// interface
interface ITitle extends IClassName, IChildren {
  size?: 'md'
  style?: Record<string, string>
  typeTag?: keyof JSX.IntrinsicElements
  classSize?: string
  animate?: boolean
}

const TitleComponent: FC<Readonly<ITitle>> = ({
  children,
  className,
  size,
  style,
  typeTag = 'h2',
  classSize,
  animate,
}) => {
  const Component: keyof JSX.IntrinsicElements = typeTag
  const parentEl = useRef(null)

  const isInView = useInView(parentEl, { once: true, amount: 0.5 })

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence>
        <Component
          className={clsx('font-extrabold', className, classSize ?? generateFonts(size))}
          style={style}
        >
          {animate
            ? (children as string)?.split(' ')?.map((word, index) => (
                <motion.span
                  key={index}
                  className={'mr-4 inline-flex flex-wrap extraMobile:flex-nowrap'}
                  ref={parentEl}
                >
                  {word?.split('')?.map((char, indexSecond) => (
                    <motion.span
                      key={indexSecond}
                      initial={{ opacity: 0, y: 20 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{
                        delay: index * 0.3 + indexSecond * 0.05,
                        duration: 0.5,
                      }}
                      style={{
                        display: 'inline-block',
                      }}
                      className={'mr-[0.4px]'}
                    >
                      {char}
                    </motion.span>
                  ))}
                </motion.span>
              ))
            : children}
        </Component>
      </AnimatePresence>
    </LazyMotion>
  )
}

export default TitleComponent
